import { useState } from "react";

import { MutationTuple } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as yup from "yup";

import {
  Button,
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
} from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import {
  MUserProfilePage_UpdateProfileMutation,
  MUserProfilePage_UpdateProfileMutationVariables,
} from "@/__generated__/graphql";
import ErrorBox from "@/components/shared/ErrorBox";
import FilePicker from "@/components/shared/FilePicker";
import UserAvatar from "@/components/UserAvatar";
import { useFormErrorHandler } from "@/hooks/useFormErrorHandler";
import { assertHasNoUserErrors } from "@/utils/legacyErrorHandler";

const UserProfileDetailsForm_User = graphql(`
  fragment UserProfileDetailsForm_User on User {
    id
    fullName
    ...UserAvatar_User
  }
`);

const formSchema = yup.object({}).shape({
  name: yup.string().required(),
  avatar: yup.mixed().notRequired(),
});

type FormValues = yup.InferType<typeof formSchema>;

interface Props {
  user: FragmentType<typeof UserProfileDetailsForm_User>;
  updateProfileMutation: MutationTuple<
    MUserProfilePage_UpdateProfileMutation,
    MUserProfilePage_UpdateProfileMutationVariables
  >;
}

export default function DetailsForm({ updateProfileMutation, ...props }: Props) {
  const user = getFragmentData(UserProfileDetailsForm_User, props.user);
  const [imagePreview, setImagePreview] = useState<string>();
  const [updateProfile, { loading }] = updateProfileMutation;

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(formSchema),
  });

  const formErrorHandler = useFormErrorHandler<typeof formSchema.fields, FormValues>({
    setError,
    formSchema,
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      const avatar = values.avatar && values.avatar[0];
      const filePromise = avatar
        ? new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = async e => {
              const tempFileData = e?.target?.result;
              resolve(tempFileData);
            };
            reader.readAsDataURL(avatar);
          })
        : null;
      const fileData = await filePromise;

      const { data } = await updateProfile({
        variables: {
          input: {
            name: values.name,
            ...(avatar && { avatar: fileData }),
          },
        },
      });

      assertHasNoUserErrors(data?.usersUpdateProfile?.userErrors);

      toast.success("Profile updated");

      reset();
    } catch (error) {
      formErrorHandler(error);
    }
  };

  const registerAvatar = register("avatar");

  return (
    <chakra.form id="update-profile" my={4} onSubmit={handleSubmit(onSubmit)}>
      <HStack alignItems="flex-end" h="120px" justifyContent="space-between" w="full">
        <UserAvatar avatarUrl={imagePreview} height="120px" mb={4} user={user} width="120px" />
        <FilePicker
          inputProps={{
            ...registerAvatar,
            accept: "image/png,image/jpg,image/jpeg,image/svg,image/webp,image/gif",
            onChange: e => {
              registerAvatar.onChange(e);
              if (e.target.files && e.target.files[0]) {
                setImagePreview(URL.createObjectURL(e.target.files[0]));
              }
            },
          }}
          maxW="xs"
        />
      </HStack>
      <FormControl isRequired isInvalid={!!errors.name}>
        <FormLabel>Full Name</FormLabel>
        <InputGroup>
          <Input aria-label="user-name" defaultValue={user?.fullName ?? ""} {...register("name")} />
        </InputGroup>
        {errors.name && <FormErrorMessage>{errors.name?.message}</FormErrorMessage>}
      </FormControl>
      {errors.root && (
        <ErrorBox header="Unexpected error" mt={4}>
          {errors.root?.message}
        </ErrorBox>
      )}
      <Button isDisabled={!isValid || !isDirty} isLoading={loading} mt={4} size="action" type="submit">
        Save Changes
      </Button>
    </chakra.form>
  );
}
